//perfectly working for desktop and functionality wise. 
import React, { useState, useEffect, useRef } from 'react';
import { IoIosSearch, IoIosCloseCircle } from "react-icons/io";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faArrowLeft, faArrowRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import CustomDropdown from '../Dropdown/index';
import SimpleDropdown from './SimpleDropdown';
import MultiSelectDropdown from '../MultiSelectDropdown/MultiSelectDropDown';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../../src/ActionPanel.scss';
import '../Table/table-sort-icon.scss';

interface ActionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  customerOptions: any[];
  tableData: any[];
}

const ActionPanel: React.FC<ActionPanelProps> = ({ isOpen, onClose, customerOptions, tableData }) => {
  const [selectedAction, setSelectedAction] = useState('None');
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [apiTableData, setApiTableData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [localCustomerOptions, setLocalCustomerOptions] = useState<any[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasChangedValue, setHasChangedValue] = useState(false);
  const isAdmin = (localStorage.getItem("jwtIsGroupAdmin") || "false") === "true";

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSelected, setPageSelected] = useState("10");
  const [sortColumn, setSortColumn] = useState('wellName');
  const [sortOrder, setSortOrder] = useState('asc');

  const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT;

  // Define table columns and headers
  const columns = ['wellName', 'customerName'];
  const columnHeaders = ['Well', 'Customer'];
  const nonSortableColumns = new Set([]); // All columns are sortable in this case

  // Function to get browser timezone
  function getBrowserTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  // Function to get setpoint ID based on action
  const getSetpointId = (action: string): number => {
    switch (action) {
      case 'Hold wells':
        return 203;
      case 'Resume wells':
        return 44;
      case 'None':
      case 'Set clock':
      case 'Check for updates':
      case 'Get device setpoints':
      default:
        return 50;
    }
  };

  // Function to get register value based on action
  const getRegisterValue = (action: string): string => {
    switch (action) {
      case 'None':
        return "0";
      case 'Set clock':
        return "1";
      case 'Check for updates':
        return "2";
      case 'Get device setpoints':
        return "6";
      case 'Hold wells':
        return "1";
      case 'Resume wells':
        return "1";
      default:
        return "0";
    }
  };

  // Handle customer selection change (mimicking Searchbar logic)
  const handleSelectionChange = (selectedValues: string[], hasChanged: boolean) => {
    setSelectedCustomers(selectedValues);
    setHasChangedValue(hasChanged);

    // Make API call when customer selection changes (and action is not 'None')
    if (selectedAction !== 'None' && hasChanged) {
      fetchTableData(selectedAction, selectedValues);
    }
  };

  // Fetch customer options when panel opens (mimicking Searchbar logic)
  useEffect(() => {
    if (isOpen && isDropdownOpen) {
      // Check if data is cached first
      const cachedCustomerOptions = sessionStorage.getItem("customerOptions");

      if (cachedCustomerOptions) {
        setLocalCustomerOptions(JSON.parse(cachedCustomerOptions));
        setIsLoadingCustomers(false);
        return;
      }

      // If not cached and customerOptions prop is empty, fetch from API
      if (customerOptions.length === 0) {
        setIsLoadingCustomers(true);
        const apiUrl = `${baseUrl}/Customer/GetAllCustomers`;

        const fetchCustomerOptions = async () => {
          const headers = {
            'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
            'Content-Type': 'application/json'
          };

          try {
            const response = await fetch(apiUrl, { headers });
            if (!response.ok) {
              throw new Error('Network response was not ok');
            }

            const data = await response.json();
            setLocalCustomerOptions(data);
            sessionStorage.setItem("customerOptions", JSON.stringify(data));
          } catch (error) {
            console.error('Error fetching customer options:', error);
          } finally {
            setIsLoadingCustomers(false);
          }
        };

        fetchCustomerOptions();
      } else {
        // Use customerOptions from props if available
        setLocalCustomerOptions(customerOptions);
        setIsLoadingCustomers(false);
      }
    }
  }, [isOpen, isDropdownOpen, customerOptions, baseUrl]);

  // Add filtering logic for table data based on selected customer and search
  const getFilteredData = () => {
    let data = selectedCustomers.length === 0
      ? apiTableData
      : apiTableData.filter(item =>
        selectedCustomers.includes(item.customerId) ||
        selectedCustomers.includes(item.companyId)
      );

    // Apply search filter
    if (searchValue) {
      const lowerCaseSearchTerm = searchValue.toLowerCase();
      data = data.filter((item) => {
        return Object.values(item).some((value) => {
          if (typeof value === 'string') {
            return value.toLowerCase().includes(lowerCaseSearchTerm);
          }
          return false;
        });
      });
    }

    return data;
  };

  const filteredTableData = getFilteredData();

  // Sorting logic (similar to EventTable)
  const sortedData = filteredTableData?.sort((a, b) => {
    const sortKey = sortColumn;
    const aValue = a[sortKey];
    const bValue = b[sortKey];

    const isANumber = !isNaN(parseFloat(aValue)) && isFinite(aValue);
    const isBNumber = !isNaN(parseFloat(bValue)) && isFinite(bValue);
    const isAEmpty = aValue === null || aValue === '';
    const isBEmpty = bValue === null || bValue === '';

    if (isAEmpty && !isBEmpty) return sortOrder === 'asc' ? -1 : 1;
    if (isBEmpty && !isAEmpty) return sortOrder === 'asc' ? 1 : -1;

    if (isANumber && isBNumber) {
      return sortOrder === 'asc'
        ? parseFloat(aValue) - parseFloat(bValue)
        : parseFloat(bValue) - parseFloat(aValue);
    } else {
      return sortOrder === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    }
  });

  // Pagination logic
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData?.slice(startIndex, endIndex);

  const actionOptions = [
    'None',
    'Hold wells',
    'Resume wells',
    'Set clock',
    'Check for updates',
    'Get device setpoints'
  ];

  // Updated API call function to handle multiple customers
  const fetchTableData = async (action: string, customerIds: string[]) => {
    setIsLoading(true);
    try {
      const setpointId = getSetpointId(action);
      const timeZoneId = getBrowserTimeZone();

      // Build the POST payload
      const payload = {
        timeZoneId: timeZoneId,
        customerId: customerIds || [], // Send array of customerIds or empty array for all
        setpointId: setpointId
      };

      const apiUrl = `${baseUrl}/Wells/GetWellsForHoldResume`;

      const headers = {
        'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract wells data from the nested structure
      let wellsData = [];
      if (data && data.length > 0 && data[0].wells) {
        wellsData = data[0].wells;
      }

      setApiTableData(wellsData);

    } catch (error) {
      console.error('API call failed:', error);
      setApiTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActionSelect = (value: string) => {
    setSelectedAction(value);

    // Make API call when action is selected (and it's not 'None')
    if (value !== 'None') {
      fetchTableData(value, selectedCustomers);
    } else {
      setApiTableData([]);
    }
  };

  const handleCustomerSelect = (value: string) => {
    setSelectedCustomers([value]);

    // Make API call when customer is changed (and action is not 'None')
    if (selectedAction !== 'None') {
      fetchTableData(selectedAction, [value]);
    }
  };

  const handleSearchChange = (searchTerm: string) => {
    setSearchValue(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  const removeSearchText = () => {
    setSearchValue("");
    setCurrentPage(1);
  };

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean) => {
    setIsAllSelected(checked);
    if (checked) {
      // Select all visible rows
      const allRowIndices = new Set(paginatedData?.map((item) => item?.assetId));
      setSelectedRows(allRowIndices);
    } else {
      // Deselect all rows
      setSelectedRows(new Set());
    }
  };

  // Handle individual row checkbox
  const handleRowSelect = (globalIndex: string, checked: boolean) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(globalIndex);
    } else {
      newSelectedRows.delete(globalIndex);
    }
    setSelectedRows(newSelectedRows);

    // Update "select all" state based on whether all rows are selected
    const currentPageIndices = paginatedData?.map((assetId) => assetId) || [];
    const allCurrentPageSelected = currentPageIndices.every(assetId => newSelectedRows.has(assetId));
    setIsAllSelected(allCurrentPageSelected && paginatedData && paginatedData.length > 0);
  };

  // Column click handler for sorting
  const handleColumnClick = (column: string) => {
    if (nonSortableColumns.has(column)) {
      return;
    }

    if (column === sortColumn) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (column: string) => {
    if (nonSortableColumns.has(column)) {
      return null;
    }

    const isActive = sortColumn === column;
    return (
      <FontAwesomeIcon
        icon={faSort}
        className={`table-sort-icon ml-1 ${isActive && sortOrder === "asc" ? "table-sort-icon--asc" : ""}`}
      />
    );
  };

  // Pagination handlers
  const handlePageNumberChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handlePageSelectedClick = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue: string = event.target.value;
    setPageSelected(selectedValue);
    const newSize = selectedValue?.toLowerCase() === "all" ? sortedData?.length || 0 : Number(selectedValue);

    const newPage = Math.floor((currentPage - 1) * pageSize / newSize) + 1;
    setPageSize(newSize);
    setCurrentPage(newPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageNumberChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil((sortedData?.length || 0) / pageSize);
    if (currentPage < totalPages) {
      handlePageNumberChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    handlePageNumberChange(page);
  };

  // Render pagination (same ellipses logic as SortTable)
  const renderPagination = () => {
    const totalPages = Math.ceil((sortedData?.length || 0) / pageSize);

    const generatePaginationNumbers = () => {
      const pages: (number | string)[] = [];
      const maxVisiblePages = 4; // Maximum visible pages excluding ellipses (Action Panel only)

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else if (currentPage <= 2) {
        pages.push(1, 2, '...', totalPages - 1, totalPages);
      } else if (currentPage > 2 && currentPage < totalPages - 1) {
        pages.push(1, '...', currentPage - 1, currentPage, '...', totalPages);
      } else {
        pages.push(1, 2, '...', totalPages - 1, totalPages);
      }

      return pages;
    };

    const paginationNumbers = generatePaginationNumbers();

    return (
      <div className="flex flex-col md:flex-row items-center justify-between mt-4">
        {/* Left aligned section: Previous button and Rows per page */}
        <div className="flex items-center gap-4">
          <button
            className={`flex items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] ${currentPage === 1
              ? "bg-darkcolor-300 cursor-not-allowed"
              : "bg-transparent text-white"
              }`}
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Previous
          </button>

          <div className="flex items-center gap-2">
            <select
              className="p-1 bg-darkcolor-600 border border-darkcolor-400 rounded text-white text-sm"
              value={pageSelected}
              onChange={handlePageSelectedClick}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="all">All</option>
            </select>
            <span className="text-sm text-white">Rows per page</span>
          </div>
        </div>

        {/* Center: Page numbers */}
        <div className="flex justify-center relative" style={{ marginRight: "70px" }}>
          {paginationNumbers.map((page, index) => (
            <button
              key={index}
              className={`mx-1 w-[30px] h-[30px] text-neutral-25 ${currentPage === page ? "rounded-full bg-darkcolor-500" : ""}`}
              onClick={() => typeof page === 'number' && handlePageClick(page)}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Right side: Next button */}
        <button
          className={`flex items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] mt-2 md:mt-0 ${currentPage === totalPages
            ? "bg-darkcolor-300 cursor-not-allowed"
            : "bg-transparent text-white"
            }`}
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
          <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
        </button>
      </div>
    );
  };

  // Handle save button click
  const handleSaveClick = () => {
    setShowConfirmModal(true);
  };

  // Toast notification functions
  const successShowToast = () => {
    toast.success('Successfully action required is completed!', {
      position: "top-right",
      autoClose: 3000,
      style: {
        color: "white",
        background: "green",
      }
    });
  };

  const errorShowToast = () => {
    toast.error('Action failed! Please try again.', {
      position: "top-right",
      autoClose: 3000,
      style: {
        color: "white",
        background: "#dc3545",
      }
    });
  };

  // Handle confirmation proceed action
  const handleConfirmProceed = async () => {
    try {
      // Prepare the API payload
      const selectedWells = sortedData.filter((item) => selectedRows.has(item?.assetId));

      const payload = {
        "Assets": selectedWells.map(well => ({
          "AssetId": well.assetId || well.wellId || well.id,
          "AssetName": well.wellName || well.assetName
        })),
        "Registers": [
          {
            "ID": getSetpointId(selectedAction),
            "Value": getRegisterValue(selectedAction)
          }
        ]
      };

      // Make the API call
      const apiUrl = `${baseUrl}/SetPointsAggregation/AddMultiAssetSetPointsData`;

      const headers = {
        'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Handle success - Show toast notification with API response data
      if (result && typeof result.successCount !== 'undefined' && typeof result.totalRecords !== 'undefined') {
        const { successCount, totalRecords } = result;

        // Determine toast type based on success ratio
        if (successCount === totalRecords) {
          // All wells successful
          toast.success(`Action successfully completed for ${successCount}/${totalRecords} wells.`, {
            position: "top-right",
            autoClose: 3000,
            style: {
              color: "white",
              background: "green",
            }
          });
        } else if (successCount > 0) {
          // Partial success
          toast.warning(`Action completed for ${successCount}/${totalRecords} wells. Some wells failed.`, {
            position: "top-right",
            autoClose: 4000,
            style: {
              color: "white",
              background: "#f59e0b", // Warning color (amber)
            }
          });
        } else {
          // No success
          toast.error(`Action failed for all ${totalRecords} wells. Please try again.`, {
            position: "top-right",
            autoClose: 4000,
            style: {
              color: "white",
              background: "#dc3545",
            }
          });
        }
      } else {
        // Fallback for unexpected response format
        toast.success('Action successfully completed!', {
          position: "top-right",
          autoClose: 3000,
          style: {
            color: "white",
            background: "green",
          }
        });
      }

    } catch (error) {
      console.error('API call failed:', error);
      // Handle error - Show error toast notification
      toast.error('Action failed! Please try again.', {
        position: "top-right",
        autoClose: 3000,
        style: {
          color: "white",
          background: "#dc3545",
        }
      });
    } finally {
      setShowConfirmModal(false);
      onClose(); // Close the panel after saving
    }
  };

  // Reset selections when data changes
  React.useEffect(() => {
    setSelectedRows(new Set());
    setIsAllSelected(false);
  }, [apiTableData, selectedCustomers]);

  // Reset data when panel closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedAction('None');
      setSelectedCustomers([]);
      setApiTableData([]);
      setSearchValue("");
      setSelectedRows(new Set());
      setIsAllSelected(false);
      setCurrentPage(1);
      setShowConfirmModal(false);
      setIsDropdownOpen(false);
      setHasChangedValue(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="action-panel__backdrop" onClick={onClose} />

      {/* Side Panel - Now uses responsive SCSS classes */}
      <div className="action-panel">
        {/* Header */}
        <div className="action-panel__header">
          <h2 className="action-panel__header-title">Actions</h2>
          <button onClick={onClose} className="action-panel__header-close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content Area - Now properly scrollable */}
        <div className="action-panel__content">
          {/* Select Required Action Dropdown */}
          <div className="action-panel__section action-panel__section--action-select">
            <h3 className="action-panel__section-title">Select required action</h3>
            <SimpleDropdown
              options={actionOptions}
              onSelect={handleActionSelect}
              defaultValue={selectedAction}
              placeholder="Select required action"
            />
          </div>

          {/* Separator */}
          <div className="action-panel__section-separator"></div>

          {/* Filter Section */}
          <div className="action-panel__section">
            <div className="action-panel__filters">

              {/* Search Input */}
              <div className="action-panel__filters-search">
                <div className="action-panel__search">
                  <IoIosSearch className="action-panel__search-icon" />
                  <div className="action-panel__search-input-wrapper">
                    <div className={`action-panel__search-placeholder ${searchValue ? 'action-panel__search-placeholder--hidden' : ''}`}>
                      Filter by keyword or by field
                    </div>
                    <input
                      onChange={(e) => handleSearchChange(e.target.value)}
                      name="filter"
                      className="action-panel__search-input"
                      value={searchValue}
                    />
                    {searchValue && (
                      <button className="action-panel__search-clear" onClick={removeSearchText}>
                        <IoIosCloseCircle />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {/* <div className="flex items-center gap-[16px]">
                <MultiSelectDropdown
                  options={localCustomerOptions}
                  placeholder="Customers"
                  onChange={handleSelectionChange}
                  isOpen={isDropdownOpen}
                  setIsOpen={setIsDropdownOpen}
                  isDropDownLoading={isLoadingCustomers}
                  isMobile={false} // or dynamically use isMobile
                />
              </div> */}
              {isAdmin && (
                <div className="flex items-center gap-[16px]">
                  <MultiSelectDropdown
                    options={localCustomerOptions}
                    placeholder="Customers"
                    onChange={handleSelectionChange}
                    isOpen={isDropdownOpen}
                    setIsOpen={setIsDropdownOpen}
                    isDropDownLoading={isLoadingCustomers}
                    isMobile={false}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Table Section */}
          {selectedAction !== 'None' && (
            <>
              <div className="action-panel__table-container">
                {sortedData && sortedData.length > 0 ? (
                  <div className="action-panel__table-wrapper">
                    <table className="action-panel__table">
                      <thead className="action-panel__table-header">
                        <tr>
                          <th className="action-panel__table-header-cell">
                            <input
                              type="checkbox"
                              className="action-panel__table-checkbox"
                              checked={isAllSelected}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          </th>
                          {columns.map((column, index) => (
                            <th
                              key={column}
                              className="action-panel__table-header-cell"
                              onClick={() => handleColumnClick(column)}
                            >
                              <div className="action-panel__table-header-cell-content">
                                {columnHeaders[index]}
                                {renderSortIcon(column)}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="action-panel__table-body">
                        {paginatedData && paginatedData.length > 0 ? (
                          paginatedData.map((item, index) => {
                            const globalIndex = startIndex + index;
                            return (
                              <tr key={globalIndex} className="action-panel__table-row">
                                <td className="action-panel__table-cell">
                                  <input
                                    type="checkbox"
                                    className="action-panel__table-checkbox"
                                    checked={selectedRows.has(item.assetId)}
                                    onChange={(e) => handleRowSelect(item.assetId, e.target.checked)}
                                  />
                                </td>
                                <td className="action-panel__table-cell">
                                  <div className="action-panel__table-cell-content">
                                    {item.wellName || 'N/A'}
                                  </div>
                                </td>
                                <td className="action-panel__table-cell">
                                  <div className="action-panel__table-cell-content">
                                    {item.customerName || 'N/A'}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={3} className="action-panel__table-cell action-panel__table-cell--empty">
                              {searchValue ? "No results found for your search" : "No data available"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Updated Pagination - Same as EventTable */}
                    {renderPagination()}
                  </div>
                ) : (
                  <div className="action-panel__no-data">
                    <div className="action-panel__no-data-content">
                      {isLoading ? "Loading..." : "No Data"}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer Buttons - Fixed at bottom, outside scrollable content */}
        {selectedAction !== 'None' && !isLoading && (
          <div className="action-panel__footer">
            <button
              className="action-panel__button action-panel__button--cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="action-panel__button action-panel__button--save"
              onClick={handleSaveClick}
              disabled={selectedRows.size === 0}
            >
              Save
            </button>
          </div>
        )}

        <ConfirmationModal
          isOpen={showConfirmModal}
          message="Are you sure you want to proceed with this action?"
          setIsValveModalOpen={setShowConfirmModal}
          handleOpenValveClick={handleConfirmProceed}
        />
      </div>

    </>
  );
};

export default ActionPanel;